import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { useState, useEffect } from 'react';

interface Showcase {
  label: string;
  title: string;
  body: string;
  bullets: string[];
  visual: React.ReactNode;
}

/* ───── Animated Visual Mockups ───── */

function ImageGridMockup() {
  const [activeIdx, setActiveIdx] = useState(0);
  // ponytail: rainbow gradient swatches replaced with ink/steel tints — one accent (cargo) marks the active tile
  const swatches = [
    '#12263A',
    '#1E3A54',
    '#5F6B7C',
    '#8A94A6',
    '#12263A',
    '#5F6B7C',
  ];

  // Cycle through images with a shimmer effect
  useEffect(() => {
    const timer = setInterval(() => setActiveIdx(p => (p + 1) % 6), 1500);
    return () => clearInterval(timer);
  }, []);

  return (
    <div
      style={{
        background: '#FFFFFF',
        border: '1px solid #D8DCE3',
        borderRadius: '12px',
        padding: '24px',
      }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
        {swatches.map((bg, i) => (
          <motion.div
            key={i}
            animate={{
              scale: activeIdx === i ? 1.05 : 1,
            }}
            transition={{ duration: 0.4 }}
            style={{
              background: bg,
              borderRadius: '8px',
              aspectRatio: '1',
              border: activeIdx === i ? '2px solid #D97757' : '2px solid rgba(18,38,58,0.15)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Shimmer sweep */}
            {activeIdx === i && (
              <motion.div
                initial={{ x: '-100%' }}
                animate={{ x: '200%' }}
                transition={{ duration: 0.8, ease: 'linear' }}
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%)',
                }}
              />
            )}
          </motion.div>
        ))}
      </div>
      {/* AI generating indicator */}
      <div style={{ marginTop: '16px', display: 'flex', gap: '10px', alignItems: 'center' }}>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          style={{
            width: '28px', height: '28px', borderRadius: '50%',
            border: '2px solid rgba(18,38,58,0.15)',
            borderTopColor: '#12263A',
          }}
        />
        <span className="font-mono" style={{ fontSize: '0.85rem', color: '#5F6B7C' }}>
          Generating image {activeIdx + 1}/6...
        </span>
      </div>
    </div>
  );
}

function CarouselMockup() {
  const [activeSlide, setActiveSlide] = useState(1);
  const slides = [
    { bg: '#1E3A54', label: 'Slide 1' },
    { bg: '#12263A', label: 'Slide 2' },
    { bg: '#5F6B7C', label: 'Slide 3' },
  ];

  useEffect(() => {
    const timer = setInterval(() => setActiveSlide(p => (p + 1) % 3), 2500);
    return () => clearInterval(timer);
  }, []);

  return (
    <div
      style={{
        background: '#FFFFFF',
        border: '1px solid #D8DCE3',
        borderRadius: '12px',
        padding: '32px 24px',
      }}
    >
      <div style={{ position: 'relative', height: '240px', display: 'flex', alignItems: 'center', justifyContent: 'center', perspective: '800px' }}>
        {slides.map((slide, i) => {
          const offset = i - activeSlide;
          return (
            <motion.div
              key={i}
              animate={{
                x: offset * 80,
                rotateY: offset * -12,
                scale: offset === 0 ? 1 : 0.85,
                opacity: offset === 0 ? 1 : 0.5,
                zIndex: offset === 0 ? 10 : 1,
              }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              style={{
                position: 'absolute',
                width: '170px',
                height: '220px',
                background: slide.bg,
                borderRadius: '10px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                padding: '16px',
              }}
            >
              {/* Fake text overlay */}
              <div style={{ height: '8px', width: '80%', background: 'rgba(255,255,255,0.6)', borderRadius: '4px', marginBottom: '8px' }} />
              <div style={{ height: '6px', width: '55%', background: 'rgba(255,255,255,0.35)', borderRadius: '3px' }} />
              {/* Slide number */}
              <div className="font-mono" style={{
                position: 'absolute', top: '12px', right: '12px',
                fontSize: '0.7rem',
                color: 'rgba(255,255,255,0.6)', background: 'rgba(0,0,0,0.3)',
                padding: '2px 8px', borderRadius: '4px',
              }}>
                {i + 1}/3
              </div>
            </motion.div>
          );
        })}
      </div>
      {/* Dots indicator */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            animate={{
              width: activeSlide === i ? 28 : 8,
              background: activeSlide === i ? '#12263A' : 'rgba(18,38,58,0.15)',
            }}
            transition={{ duration: 0.3 }}
            style={{ height: '8px', borderRadius: '4px' }}
          />
        ))}
      </div>
    </div>
  );
}

function AnalyticsMockup() {
  const stats = [
    { label: 'Posts', value: 247, color: '#12263A' },
    { label: 'Engagement', value: 89, color: '#5F6B7C', suffix: '%' },
    { label: 'Reach', value: 12400, color: '#8A94A6', format: true },
  ];
  const barHeights = [60, 85, 45, 70, 95];

  return (
    <div
      style={{
        background: '#FFFFFF',
        border: '1px solid #D8DCE3',
        borderRadius: '12px',
        padding: '24px',
      }}
    >
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
        {stats.map((stat, i) => (
          <div key={i} style={{ flex: 1, background: '#F7F6F2', borderRadius: '8px', padding: '14px', borderLeft: `3px solid ${stat.color}` }}>
            <div className="font-mono" style={{ fontSize: '0.75rem', color: '#5F6B7C', textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>
              {stat.label}
            </div>
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="font-display"
              style={{ fontSize: '1.5rem', color: '#12263A', marginTop: '4px' }}
            >
              {stat.format ? '12.4K' : stat.value}{stat.suffix || ''}
            </motion.div>
          </div>
        ))}
      </div>
      {/* Animated bar chart */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', height: '120px', padding: '0 8px' }}>
        {barHeights.map((h, i) => (
          <motion.div
            key={i}
            initial={{ height: 0 }}
            whileInView={{ height: `${h}%` }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
            style={{
              flex: 1,
              borderRadius: '6px 6px 0 0',
              background: i % 2 === 0 ? '#12263A' : '#5F6B7C',
            }}
          />
        ))}
      </div>
    </div>
  );
}

function CalendarMockup() {
  const highlightedDays = [2, 5, 8, 11, 15, 18, 22, 25];
  const markedDays = [5, 15, 22];
  const ideas = [
    { title: 'Product Launch Post', color: '#12263A' },
    { title: 'Industry Trends Thread', color: '#5F6B7C' },
    { title: 'Customer Case Study', color: '#8A94A6' },
  ];

  return (
    <div
      style={{
        background: '#FFFFFF',
        border: '1px solid #D8DCE3',
        borderRadius: '12px',
        padding: '24px',
      }}
    >
      <div style={{ display: 'flex', gap: '16px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '6px' }}>
            {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
              <div key={i} className="font-mono" style={{ textAlign: 'center' as const, fontSize: '0.7rem', color: 'rgba(18,38,58,0.5)', paddingBottom: '4px' }}>
                {d}
              </div>
            ))}
            {Array.from({ length: 28 }, (_, i) => {
              const day = i + 1;
              const isHighlighted = highlightedDays.includes(day);
              const isMarked = markedDays.includes(day);
              const delayIdx = highlightedDays.indexOf(day);
              return (
                <motion.div
                  key={i}
                  initial={isHighlighted || isMarked ? { scale: 0, opacity: 0 } : {}}
                  whileInView={isHighlighted || isMarked ? { scale: 1, opacity: 1 } : {}}
                  viewport={{ once: true }}
                  transition={isHighlighted || isMarked ? { duration: 0.4, delay: (delayIdx >= 0 ? delayIdx : 0) * 0.08 + 0.3, type: 'spring', stiffness: 400, damping: 15 } : {}}
                  style={{
                    width: '100%',
                    aspectRatio: '1',
                    borderRadius: '5px',
                    background: isMarked ? 'rgba(217,119,87,0.18)' : isHighlighted ? 'rgba(18,38,58,0.12)' : 'rgba(18,38,58,0.03)',
                    border: isHighlighted || isMarked ? `1px solid ${isMarked ? 'rgba(217,119,87,0.5)' : 'rgba(18,38,58,0.3)'}` : '1px solid transparent',
                  }}
                />
              );
            })}
          </div>
        </div>
        {/* Idea cards that slide in */}
        <div style={{ width: '130px', display: 'flex', flexDirection: 'column' as const, gap: '10px' }}>
          {ideas.map((idea, i) => (
            <motion.div
              key={i}
              initial={{ x: 30, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.5 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
              style={{
                background: '#F7F6F2',
                borderRadius: '8px',
                padding: '10px 12px',
                borderLeft: `3px solid ${idea.color}`,
              }}
            >
              <div className="font-body" style={{ fontSize: '0.8rem', color: '#12263A', lineHeight: 1.3, fontWeight: 500 }}>
                {idea.title}
              </div>
              <div className="font-mono" style={{ fontSize: '0.65rem', color: '#5F6B7C', marginTop: '4px' }}>
                AI suggested
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ───── Data ───── */

const showcases: Showcase[] = [
  {
    label: 'AI-POWERED VISUALS',
    title: 'Generate Stunning Images for Every Post',
    body: 'Get AI-generated images that perfectly match your brand and message. Choose from stock suggestions, upload your own, or let ZUPA generate context-aware custom images with text overlays.',
    bullets: [
      'Context-aware AI image generation',
      'Custom text overlay editor',
      'Brand-aligned visual suggestions',
    ],
    visual: <ImageGridMockup />,
  },
  {
    label: 'MULTI-SLIDE CONTENT',
    title: 'Build Scroll-Stopping Carousels',
    body: 'Create engaging multi-slide carousel posts with AI-generated images and text overlays. ZUPA plans the narrative arc first, then generates each slide with perfectly matched visuals.',
    bullets: [
      'AI-planned narrative arcs',
      'Slide-by-slide generation',
      'Custom text overlays per slide',
    ],
    visual: <CarouselMockup />,
  },
  {
    label: 'TRACK & OPTIMIZE',
    title: 'Your Content Command Center',
    body: 'Monitor your generation history, track which psychological principles drive the most engagement, and see your content performance across all platforms from a single dashboard.',
    bullets: [
      'Generation history & analytics',
      'Psychology principle effectiveness',
      'Cross-platform performance tracking',
    ],
    visual: <AnalyticsMockup />,
  },
  {
    label: 'PLAN & IDEATE',
    title: 'Never Run Out of Content Ideas',
    body: 'Plan your content strategy with a visual calendar, add notes to specific dates, and get AI-suggested content topics based on your brand, industry trends, and audience preferences.',
    bullets: [
      'Visual content calendar with notes',
      'AI-suggested content topics',
      'Trend-based ideation engine',
    ],
    visual: <CalendarMockup />,
  },
];

/* ───── Main Component ───── */

export function FeatureShowcaseSection() {
  return (
    <>
      {showcases.map((showcase, i) => {
        const isReversed = i % 2 !== 0;
        const bgColor = i % 2 === 0 ? '#F7F6F2' : '#FFFFFF';

        return (
          <section
            key={i}
            style={{
              backgroundColor: bgColor,
              padding: 'clamp(64px, 10vw, 120px) 0',
            }}
          >
            <div
              style={{
                maxWidth: '1200px',
                margin: '0 auto',
                padding: '0 24px',
              }}
            >
              <div
                className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16"
              >
                {/* Text side */}
                <motion.div
                  className={`w-full lg:w-1/2 ${isReversed ? 'lg:order-2' : ''}`}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                  <span
                    className="font-mono uppercase"
                    style={{
                      fontSize: 'clamp(0.9rem, 1vw, 1.1rem)',
                      letterSpacing: '0.12em',
                      color: '#5F6B7C',
                      display: 'block',
                      marginBottom: '16px',
                    }}
                  >
                    {showcase.label}
                  </span>

                  <h2
                    className="font-display"
                    style={{
                      fontSize: 'clamp(2rem, 4vw + 0.5rem, 3rem)',
                      color: '#12263A',
                      lineHeight: 1.15,
                      marginBottom: '20px',
                    }}
                  >
                    {showcase.title}
                  </h2>

                  <p
                    className="font-body"
                    style={{
                      fontSize: 'clamp(1.25rem, 1vw + 0.9rem, 1.5rem)',
                      color: '#5F6B7C',
                      lineHeight: 1.6,
                      maxWidth: '540px',
                      marginBottom: '28px',
                    }}
                  >
                    {showcase.body}
                  </p>

                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {showcase.bullets.map((bullet, j) => (
                      <li
                        key={j}
                        className="font-body"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          fontSize: '1.2rem',
                          color: '#5F6B7C',
                        }}
                      >
                        <Check
                          size={20}
                          style={{
                            color: '#12263A',
                            flexShrink: 0,
                          }}
                        />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                </motion.div>

                {/* Visual side */}
                <motion.div
                  className={`w-full lg:w-1/2 ${isReversed ? 'lg:order-1' : ''}`}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                >
                  {showcase.visual}
                </motion.div>
              </div>
            </div>
          </section>
        );
      })}
    </>
  );
}
